import { QueryClient } from '@tanstack/react-query';
import { beginReauth, onReauth } from '../auth/auth0Client';
import { isRyptError } from '../api/errors';

/**
 * Retry policy.
 *
 * Never retry a 4xx: the request was wrong and will be wrong again. The two exceptions
 * are the 429s, and only one of those — ops_cap_reached is a fact about the month, not a
 * transient. 503 unavailable IS retried, because it often means rypt could not reach
 * Auth0's JWKS, which resolves on its own.
 */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') return false;
  if (!isRyptError(error)) return failureCount < 1;

  switch (error.kind) {
    case 'auth':
    case 'config':
      return false; // another attempt produces the same bad token
    case 'transport':
      return failureCount < 2; // a cold Cloud Run instance 502s once
    case 'api':
      break;
  }

  if (error.status === 401) return false; // re-auth, not retry
  if (error.code === 'ops_cap_reached') return false;
  if (error.code === 'rate_limited' || error.code === 'unavailable') return failureCount < 4;
  if (error.status >= 500) return failureCount < 1;
  return false;
}

/**
 * Blind backoff. rypt sends no Retry-After and no X-RateLimit-* headers, so there is
 * nothing to obey and the client has to guess. Full jitter, so several tabs that hit the
 * limit together do not retry together and hit it again.
 */
export function blindBackoff(attempt: number, error: unknown): number {
  const base = isRyptError(error) && error.code === 'rate_limited' ? 500 : 1_000;
  const ceiling = Math.min(base * 2 ** attempt, 20_000);
  return Math.round(ceiling * (0.5 + Math.random() / 2));
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetry,
      retryDelay: blindBackoff,
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      throwOnError: false,
    },
    mutations: {
      // Never replay a POST /v1/keys or a delete. A retried mutation on key material is
      // a second key or a second destruction, not a recovered request.
      retry: 0,
      throwOnError: false,
    },
  },
});

// Deliberately no persistQueryClient, ever. POST /v1/api-tokens is the only response that
// carries a secret, and the mutation strips it before the cache — but a persisted cache
// is a standing invitation to undo that, so the capability is simply absent.

function onGlobalError(error: unknown): void {
  if (isRyptError(error) && error.isReauth) beginReauth();
}

queryClient.getQueryCache().config.onError = onGlobalError;
queryClient.getMutationCache().config.onError = onGlobalError;

// Sign-out and re-auth both drop everything: the cache is per-tab and would otherwise
// show one tenant's keys to whoever signs in next.
onReauth(() => {
  void queryClient.cancelQueries();
  queryClient.clear();
});
